import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';

const Home = () => {
  const { currentUser } = useAuth();

  return (
    <div className="-mx-4 -mt-8"> {/* Bleed out of container padding */}

      {/* ========== HERO SECTION ========== */}
      <section className="ste-gradient-bg py-20 md:py-28 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-navy-800/60 border border-navy-600 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 bg-signal-bullish rounded-full animate-pulse"></span>
            <span className="text-sm text-navy-200">AI-Powered Signal Intelligence</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
            Stop Guessing.<br />
            <span className="ste-gradient-text">Start Trading with an Edge.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-navy-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Swing Trader Edge distills dozens of technical indicators into one clear 
            <strong className="text-white"> Signal Score</strong> — plus an AI-powered 
            <strong className="text-white"> Predictive Score</strong> that anticipates trend shifts 
            before they happen.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {currentUser ? (
              <Link to="/dashboard" className="ste-btn-primary text-lg px-8 py-4">
                Go to Dashboard →
              </Link>
            ) : (
              <>
                <Link to="/login" className="ste-btn-primary text-lg px-8 py-4">
                  Get Started Free →
                </Link>
                <a href="#how-it-works" className="ste-btn-secondary text-lg px-8 py-4 !bg-transparent !border-navy-500 !text-navy-200 hover:!border-brand-400 hover:!text-white">
                  See How It Works
                </a>
              </>
            )}
          </div>

          <p className="text-navy-500 text-sm mt-4">No credit card required • Free tier available</p>
        </div>
      </section>

      {/* ========== PAIN POINT SECTION ========== */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              Technical Analysis Shouldn't Be This Hard
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Swing traders face an overwhelming flood of indicators, chart patterns, and conflicting signals. 
              Studies show <strong>35% of investors</strong> cite timing decisions as their biggest challenge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-signal-bearish-light rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-signal-bearish" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Information Overload</h3>
              <p className="text-gray-600 text-sm">RSI, MACD, Bollinger Bands, moving averages, volume profiles... which ones matter right now?</p>
            </div>

            <div className="text-center p-6">
              <div className="w-14 h-14 bg-signal-neutral-light rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-signal-neutral" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Timing Is Everything</h3>
              <p className="text-gray-600 text-sm">Enter too early, exit too late. Without clear signals, emotion drives decisions — and eats returns.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Not Enough Hours</h3>
              <p className="text-gray-600 text-sm">You're not a day trader. You need a tool that works as hard as you do — scanning hundreds of stocks overnight.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SOLUTION: SIGNAL SCORE ========== */}
      <section className="py-16 md:py-20 px-4 bg-navy-50" id="how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              One Score. Clearer Trends.
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The Signal Score is a <strong>signal strength indicator</strong> from 0 to 5. The number by itself is not a "buy" or "sell".
              What matters is <strong>when the score hits a new extreme</strong> relative to the current trend.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Signal Score Visual */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Signal Score Breakdown</h3>
              <p className="text-sm text-gray-600 mb-6">
                SwingTraderEdge is built to help you spot <strong>when new major trends begin and end</strong>. The Signal Score is one key ingredient in confirming those trend shifts.
              </p>
              
              {/* Score meter */}
              <div className="flex items-center justify-between mb-6">
                {[0, 1, 2, 3, 4, 5].map((score) => (
                  <div key={score} className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white ${
                      score === 0 || score === 1 ? 'bg-signal-bearish' : score === 2 || score === 3 || score === 4 ? 'bg-signal-neutral' : 'bg-signal-bullish'
                    }`}>
                      {score}
                    </div>
                    <span className="text-xs text-gray-500 mt-2">
                      {score === 0 ? 'Downtrend confirm' : score === 5 ? 'Uptrend confirm' : 'Neutral / continue'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="text-xs text-gray-500 mb-6">
                <strong>Key idea:</strong> the most important signals are <strong>new extremes</strong>. A <strong>new 5</strong> confirms a new uptrend (when coming from a downtrend), and a <strong>new 0</strong> confirms a new downtrend (when coming from an uptrend). Scores in the <strong>14</strong> range generally reflect continuation / neutral strength within the current trend.
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <span className="w-3 h-3 bg-brand-500 rounded-full mr-3"></span>
                  <span className="text-gray-700"><strong>New 5</strong> during a downtrend confirms a new uptrend (potential entry context)</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="w-3 h-3 bg-brand-500 rounded-full mr-3"></span>
                  <span className="text-gray-700"><strong>New 0</strong> during an uptrend confirms a new downtrend (potential exit context)</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="w-3 h-3 bg-brand-500 rounded-full mr-3"></span>
                  <span className="text-gray-700">Scores <strong>14</strong> usually indicate neutral/continuation of the current trend</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="w-3 h-3 bg-brand-500 rounded-full mr-3"></span>
                  <span className="text-gray-700">The Predictive Score is a more sensitive, medium-term companion to the longer-term Signal Score</span>
                </div>
              </div>
            </div>

            {/* Predictive Score */}
            <div>
              <div className="mb-8">
                <div className="inline-flex items-center bg-brand-100 text-brand-700 rounded-full px-3 py-1 text-sm font-medium mb-4">
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  AI-Enhanced
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-3">Predictive Signal Score</h3>
                <p className="text-gray-600 leading-relaxed">
                  The AI Predictive Score is a more sensitive indicator than the Signal Score. It uses a predictive model to forecast
                  near-term price/volume, then applies the same style of indicator blend over a slightly shorter window.
                  Think of the Signal Score as the longer-term trend context, and the Predictive Score as a smarter medium-term indicator.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-signal-bullish-light rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <svg className="w-5 h-5 text-signal-bullish" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-900 text-sm">Early Trend Detection</h4>
                    <p className="text-gray-600 text-sm">Spot emerging uptrends and downtrends before conventional indicators confirm them.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-signal-bullish-light rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <svg className="w-5 h-5 text-signal-bullish" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-900 text-sm">Direction Prediction</h4>
                    <p className="text-gray-600 text-sm">Clear up/down directional forecast alongside the signal strength rating.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-signal-bullish-light rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <svg className="w-5 h-5 text-signal-bullish" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-900 text-sm">Updated Nightly</h4>
                    <p className="text-gray-600 text-sm">Scores refresh every trading day, so you always have the latest intelligence.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== NEW TREND EXAMPLES ========== */}
      <section className="py-16 md:py-20 px-4 bg-white" id="examples">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">New Trend Examples</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              SwingTraderEdge is focused on identifying when <strong>new major trends begin and end</strong>. The most meaningful confirmations happen when the Signal Score hits a <strong>new 5</strong> or a <strong>new 0</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* New Uptrend */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-green-50 border-b border-green-100 px-6 py-4">
                <div className="text-sm font-semibold text-green-800">New Uptrend example</div>
                <div className="text-xs text-green-700 mt-1">Signal Score: 5 • AI Predictive Score: 5</div>
              </div>
              <div className="p-6">
                <div className="aspect-[16/10] rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                    Placeholder image (green_example.jpg)
                  </div>
                </div>
                <p className="text-sm text-gray-700 mt-4 leading-relaxed">
                  In the graph above, the Signal Strength indicator and the AI Predictive Signal Strength indicators are 5, showing that all key technical indicators are flashing positive.
                </p>
                <p className="text-xs text-gray-500 mt-3">
                  The vertical bar on the right side of the graphs shows the percent gap between the current trading range and the prior peak in the last 6 months.
                </p>
              </div>
            </div>

            {/* New Downtrend */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-red-50 border-b border-red-100 px-6 py-4">
                <div className="text-sm font-semibold text-red-800">New Downtrend example</div>
                <div className="text-xs text-red-700 mt-1">Signal Score: 0 • AI Predictive Score: 0</div>
              </div>
              <div className="p-6">
                <div className="aspect-[16/10] rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                    Placeholder image (red_example.jpg)
                  </div>
                </div>
                <p className="text-sm text-gray-700 mt-4 leading-relaxed">
                  In the graph above, both the Signal Strength indicator and the AI Predictive Strength indicators are 0, indicating a continued major downtrend.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              Simple. Powerful. Actionable.
            </h2>
            <p className="text-lg text-gray-600">Three steps to better trading decisions.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-5 text-2xl font-bold shadow-glow">
                1
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Scan the Dashboard</h3>
              <p className="text-gray-600 text-sm">
                Browse color-coded charts for hundreds of stocks and ETFs. Green means bullish, red means bearish — at a glance.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-5 text-2xl font-bold shadow-glow">
                2
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Filter & Focus</h3>
              <p className="text-gray-600 text-sm">
                Use smart filters to narrow down: high signal scores, new uptrends, oversold bounces, sector-specific screens, and more.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-5 text-2xl font-bold shadow-glow">
                3
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">Act with Confidence</h3>
              <p className="text-gray-600 text-sm">
                Dive into any stock for detailed technical and fundamental metrics. Save filter presets and build your watchlist.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PRICING ========== */}
      <section className="py-16 md:py-20 px-4 bg-navy-50" id="pricing">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              Start Free. Upgrade When You're Ready.
            </h2>
            <p className="text-lg text-gray-600">No tricks. No hidden fees. Cancel anytime.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Free Tier */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
              <h3 className="text-lg font-semibold text-navy-900 mb-1">Free</h3>
              <p className="text-gray-500 text-sm mb-6">Get started with the basics</p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-navy-900">$0</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-signal-bullish mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Signal Score for all stocks
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-signal-bullish mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Color-coded chart grid
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-signal-bullish mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Basic filtering
                </li>
                <li className="flex items-center text-sm text-gray-400">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  Predictive Signal Score
                </li>
                <li className="flex items-center text-sm text-gray-400">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  Watchlists & Alerts
                </li>
              </ul>
              
              <Link to="/login" className="ste-btn-secondary w-full text-center">
                Sign Up Free
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-brand-500 p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                MOST POPULAR
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-1">Pro</h3>
              <p className="text-gray-500 text-sm mb-6">Full intelligence suite</p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-navy-900">$10</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-signal-bullish mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Everything in Free
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-brand-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <strong>AI Predictive Signal Score</strong>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-brand-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <strong>Smart filter presets</strong> (unlimited)
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-brand-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <strong>Watchlists & email alerts</strong>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-brand-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <strong>CSV export</strong>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 text-brand-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <strong>Historical signal data</strong>
                </li>
              </ul>
              
              <Link to="/login" className="ste-btn-primary w-full text-center">
                Start Free Trial →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SOCIAL PROOF ========== */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-12">
            Trusted by Swing Traders
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-navy-50 rounded-xl p-6">
              <div className="flex items-center justify-center mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5 text-signal-neutral" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 text-sm italic mb-3">"The Signal Score saves me hours every week. I used to flip between five different tools — now I just check STE."</p>
              <p className="text-navy-900 text-sm font-semibold">— Early Beta User</p>
            </div>

            <div className="bg-navy-50 rounded-xl p-6">
              <div className="flex items-center justify-center mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5 text-signal-neutral" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 text-sm italic mb-3">"The Predictive Score caught a trend reversal in NVDA two days before my other tools flagged it."</p>
              <p className="text-navy-900 text-sm font-semibold">— Early Beta User</p>
            </div>

            <div className="bg-navy-50 rounded-xl p-6">
              <div className="flex items-center justify-center mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5 text-signal-neutral" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 text-sm italic mb-3">"I'm not a technical analyst. STE makes me feel like one. The color coding is genius."</p>
              <p className="text-navy-900 text-sm font-semibold">— Early Beta User</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="ste-gradient-bg py-16 md:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Trade with an Edge?
          </h2>
          <p className="text-lg text-navy-300 mb-8">
            Join hundreds of swing traders using AI-powered signals to make smarter decisions.
          </p>
          {currentUser ? (
            <Link to="/dashboard" className="ste-btn-primary text-lg px-8 py-4">
              Go to Dashboard →
            </Link>
          ) : (
            <Link to="/login" className="ste-btn-primary text-lg px-8 py-4">
              Get Started Free →
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
